import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { Header } from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { buildApiUrl } from '../services/api';

interface Settings {
  id: string;
  openaiApiKey?: string;
  groqApiKey?: string;
  claudeApiKey?: string;
  geminiApiKey?: string;
}

const settingsSchema = z.object({
  openaiApiKey: z.string().optional(),
  groqApiKey: z.string().optional(),
  claudeApiKey: z.string().optional(),
  geminiApiKey: z.string().optional(),
});

type SettingsFormData = z.infer<typeof settingsSchema>;
type AIProviderKey = 'openai' | 'groq' | 'claude' | 'gemini';
type AIProviderField = keyof SettingsFormData;

type AIProviderConfig = {
  key: AIProviderKey;
  title: string;
  subtitle: string;
  badge: string;
  badgeConfiguredClass: string;
  badgePendingClass: string;
  cardClass: string;
  iconClass: string;
  iconLabel: string;
  buttonClass: string;
  modalTitle: string;
  fieldKey: AIProviderField;
  fieldLabel: string;
  placeholder: string;
  helperText: string;
};

const AI_PROVIDER_CONFIGS: Record<AIProviderKey, AIProviderConfig> = {
  openai: {
    key: 'openai',
    title: 'OpenAI',
    subtitle: 'ChatGPT, GPT-4',
    badge: 'AI',
    badgeConfiguredClass: 'bg-emerald-100 text-emerald-800',
    badgePendingClass: 'bg-gray-100 text-gray-800',
    cardClass: 'border-emerald-200 bg-emerald-50/40 hover:bg-emerald-50',
    iconClass: 'bg-emerald-100 text-emerald-700',
    iconLabel: 'AI',
    buttonClass: 'bg-gray-800 hover:bg-gray-900',
    modalTitle: '🤖 Configurar OpenAI',
    fieldKey: 'openaiApiKey',
    fieldLabel: 'API Key OpenAI',
    placeholder: 'sk-...',
    helperText: 'Chave API para integração com ChatGPT nas campanhas',
  },
  groq: {
    key: 'groq',
    title: 'Groq',
    subtitle: 'LLaMA, Mixtral (ultra-rápido)',
    badge: 'GQ',
    badgeConfiguredClass: 'bg-green-100 text-green-800',
    badgePendingClass: 'bg-gray-100 text-gray-800',
    cardClass: 'border-amber-200 bg-amber-50/40 hover:bg-amber-50',
    iconClass: 'bg-orange-100 text-orange-700',
    iconLabel: 'GQ',
    buttonClass: 'bg-orange-600 hover:bg-orange-700',
    modalTitle: '⚡ Configurar Groq',
    fieldKey: 'groqApiKey',
    fieldLabel: 'API Key Groq',
    placeholder: 'gsk_...',
    helperText: 'Chave API para integração com Groq AI nas campanhas (modelos rápidos e eficientes)',
  },
  claude: {
    key: 'claude',
    title: 'Claude AI',
    subtitle: 'Anthropic Claude',
    badge: 'CL',
    badgeConfiguredClass: 'bg-violet-100 text-violet-800',
    badgePendingClass: 'bg-gray-100 text-gray-800',
    cardClass: 'border-violet-200 bg-violet-50/40 hover:bg-violet-50',
    iconClass: 'bg-violet-100 text-violet-700',
    iconLabel: 'CL',
    buttonClass: 'bg-violet-600 hover:bg-violet-700',
    modalTitle: '🧠 Configurar Claude AI',
    fieldKey: 'claudeApiKey',
    fieldLabel: 'API Key Claude',
    placeholder: 'sk-ant-...',
    helperText: 'Chave API para integração com Claude nas campanhas e certidões',
  },
  gemini: {
    key: 'gemini',
    title: 'Google Gemini',
    subtitle: 'Gemini 1.5 Flash/Pro',
    badge: 'GM',
    badgeConfiguredClass: 'bg-sky-100 text-sky-800',
    badgePendingClass: 'bg-gray-100 text-gray-800',
    cardClass: 'border-sky-200 bg-sky-50/40 hover:bg-sky-50',
    iconClass: 'bg-blue-100 text-blue-700',
    iconLabel: 'GM',
    buttonClass: 'bg-blue-600 hover:bg-blue-700',
    modalTitle: '✦ Configurar Google Gemini',
    fieldKey: 'geminiApiKey',
    fieldLabel: 'API Key Gemini',
    placeholder: 'AIza...',
    helperText: 'Chave API para integração com Google Gemini nas campanhas e certidões',
  },
};

const PROVIDER_ORDER: AIProviderKey[] = ['openai', 'groq', 'claude', 'gemini'];

export function SettingsPage() {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeModal, setActiveModal] = useState<AIProviderKey | null>(null);
  const { user } = useAuth();

  // Helper para fazer requisições autenticadas
  const authenticatedFetch = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('auth_token');
    const selectedTenantId = localStorage.getItem('superadmin_selected_tenant') || localStorage.getItem('selected_tenant_id');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    if (selectedTenantId && !(headers as Record<string, string>)['X-Tenant-Id']) {
      (headers as Record<string, string>)['X-Tenant-Id'] = selectedTenantId;
    }

    return fetch(buildApiUrl(url), {
      ...options,
      headers,
    });
  };

  const getProviderConfig = (provider: AIProviderKey) => AI_PROVIDER_CONFIGS[provider];

  const isProviderConfigured = (provider: AIProviderKey) => {
    const fieldKey = getProviderConfig(provider).fieldKey;
    return Boolean(settings?.[fieldKey]);
  };

  const renderProviderCard = (provider: AIProviderKey) => {
    const config = getProviderConfig(provider);
    const configured = isProviderConfigured(provider);

    return (
      <div key={provider} className={`border rounded-lg p-4 transition-colors ${config.cardClass}`}>
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${config.iconClass}`}>
              <span className="font-semibold">{config.iconLabel}</span>
            </div>
            <div className="min-w-0">
              <h3 className="font-medium text-gray-900">{config.title}</h3>
              <p className="text-sm text-gray-500">{config.subtitle}</p>
            </div>
          </div>

          <span
            className={`inline-flex self-start px-2 py-1 text-xs font-semibold rounded-full ${
              configured ? config.badgeConfiguredClass : config.badgePendingClass
            }`}
          >
            {configured ? 'Configurado' : 'Não configurado'}
          </span>

          <button
            onClick={() => setActiveModal(provider)}
            className={`w-full px-3 py-2 text-sm rounded text-white transition-colors ${
              configured ? config.buttonClass : 'bg-gray-800 hover:bg-gray-900'
            }`}
          >
            {configured ? 'Editar' : 'Configurar'}
          </button>
        </div>
      </div>
    );
  };


  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<SettingsFormData>({
    resolver: zodResolver(settingsSchema),
  });

  useEffect(() => {
    loadSettings();

    // Listen for tenant changes from header selector
    const handleTenantChange = () => {
      loadSettings();
    };

    window.addEventListener('superadmin-tenant-changed', handleTenantChange);
    return () => {
      window.removeEventListener('superadmin-tenant-changed', handleTenantChange);
    };
  }, [user]);

  const loadSettings = async () => {
    try {
      let url = '/settings';

      if (user?.role === 'SUPERADMIN') {
        const selectedTenantId = localStorage.getItem('superadmin_selected_tenant');
        if (selectedTenantId) {
          url = `/settings?tenantId=${selectedTenantId}`;
        }
      }

      const response = await authenticatedFetch(url);
      if (response.ok) {
        const data = await response.json();
        console.log('[settings:load] response', {
          url,
          role: user?.role,
          selectedTenantId: localStorage.getItem('superadmin_selected_tenant') || localStorage.getItem('selected_tenant_id'),
          openaiConfigured: !!data.openaiApiKey,
          groqConfigured: !!data.groqApiKey,
          claudeConfigured: !!data.claudeApiKey,
          geminiConfigured: !!data.geminiApiKey,
        });
        setSettings(data);
        setValue('openaiApiKey', data.openaiApiKey || '');
        setValue('groqApiKey', data.groqApiKey || '');
        setValue('claudeApiKey', data.claudeApiKey || '');
        setValue('geminiApiKey', data.geminiApiKey || '');
      }
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
      toast.error('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: SettingsFormData) => {
    try {
      const requestData: Record<string, unknown> = {};
      const selectedTenantId = localStorage.getItem('superadmin_selected_tenant') || localStorage.getItem('selected_tenant_id');

      if (activeModal === 'openai') {
        requestData.openaiApiKey = (data.openaiApiKey || '').trim();
      }

      if (activeModal === 'groq') {
        requestData.groqApiKey = (data.groqApiKey || '').trim();
      }

      if (activeModal === 'claude') {
        requestData.claudeApiKey = (data.claudeApiKey || '').trim();
      }

      if (activeModal === 'gemini') {
        requestData.geminiApiKey = (data.geminiApiKey || '').trim();
      }

      if (selectedTenantId) {
        requestData.tenantId = selectedTenantId;
      }

      console.log('[settings:save] request', {
        activeModal,
        role: user?.role,
        selectedTenantId,
        hasOpenAIKey: typeof requestData.openaiApiKey === 'string' && requestData.openaiApiKey.length > 0,
        hasGroqKey: typeof requestData.groqApiKey === 'string' && requestData.groqApiKey.length > 0,
        hasClaudeKey: typeof requestData.claudeApiKey === 'string' && requestData.claudeApiKey.length > 0,
        hasGeminiKey: typeof requestData.geminiApiKey === 'string' && requestData.geminiApiKey.length > 0,
      });

      const response = await authenticatedFetch('/settings', {
        method: 'PUT',
        body: JSON.stringify(requestData),
      });

      if (response.ok) {
        const responseData = await response.json().catch(() => ({}));
        console.log('[settings:save] response', {
          activeModal,
          openaiConfigured: !!responseData?.settings?.openaiApiKey,
          groqConfigured: !!responseData?.settings?.groqApiKey,
          claudeConfigured: !!responseData?.settings?.claudeApiKey,
          geminiConfigured: !!responseData?.settings?.geminiApiKey,
        });
        if (responseData?.settings) {
          setSettings(responseData.settings);
        }
        toast.success('Configurações de integração salvas com sucesso');
        setActiveModal(null);
        await loadSettings();
      } else {
        const errorData = await response.json().catch(() => ({}));
        const firstValidation = Array.isArray(errorData?.errors) ? errorData.errors?.[0]?.msg : null;
        toast.error(firstValidation || errorData?.error || 'Erro ao salvar configurações de integração');
      }
    } catch (error) {
      console.error('Erro ao salvar configurações de integração:', error);
      const message = error instanceof Error ? error.message : 'Erro ao salvar configurações de integração';
      toast.error(message);
    }
  };

  const removeIntegration = async (type: AIProviderKey) => {
      if (!confirm(`Tem certeza que deseja remover a integração com ${getProviderConfig(type).title}?`)) {
      return;
    }

    try {
      let requestData: any = {};

      if (type === 'openai') {
        requestData.openaiApiKey = '';
      } else if (type === 'groq') {
        requestData.groqApiKey = '';
      } else if (type === 'claude') {
        requestData.claudeApiKey = '';
      } else {
        requestData.geminiApiKey = '';
      }

      if (user?.role === 'SUPERADMIN') {
        const selectedTenantId = localStorage.getItem('superadmin_selected_tenant');
        if (selectedTenantId) {
          requestData.tenantId = selectedTenantId;
        }
      }

      const response = await authenticatedFetch('/settings', {
        method: 'PUT',
        body: JSON.stringify(requestData),
      });

      if (response.ok) {
        toast.success(`Integração com ${getProviderConfig(type).title} removida com sucesso`);
        setActiveModal(null);
        await loadSettings();
      } else {
        const errorData = await response.json().catch(() => ({}));
        const firstValidation = Array.isArray(errorData?.errors) ? errorData.errors?.[0]?.msg : null;
        toast.error(firstValidation || errorData?.error || 'Erro ao remover integração');
      }
    } catch (error) {
      console.error('Erro ao remover integração:', error);
      const message = error instanceof Error ? error.message : 'Erro ao remover integração';
      toast.error(message);
    }
  };


  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Carregando configurações...</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Header
        title="Configurações"
        subtitle="Configure as definições do sistema"
      />

      <div className="p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-6 text-gray-900">
              🔗 Integrações de IA
            </h2>
            <p className="text-gray-600 mb-4">
              Configure as chaves de API para usar inteligência artificial nas campanhas
            </p>

            <div className="mb-6 rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900">
              Dica: você pode configurar múltiplos provedores. Quando um estiver indisponível, o sistema tenta fallback automático.
            </div>


            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {PROVIDER_ORDER.map(renderProviderCard)}
            </div>
          </div>
        </div>
      </div>

      {activeModal && (() => {
        const config = getProviderConfig(activeModal);
        const fieldKey = config.fieldKey;
        const fieldError = errors[fieldKey];
        const currentValue = settings?.[fieldKey] || '';

        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center p-4 sm:p-6 z-50 backdrop-blur-sm overflow-y-auto">
            <div className="bg-white rounded-lg p-5 sm:p-6 w-full max-w-md max-h-[calc(100vh-2rem)] overflow-y-auto">
              <div className="flex justify-between items-start gap-4 mb-4">
                <div className="min-w-0">
                  <h3 className="text-lg font-semibold text-gray-900">{config.modalTitle}</h3>
                  <p className="text-sm text-gray-500 mt-1">{config.subtitle}</p>
                </div>
                <button
                  onClick={() => setActiveModal(null)}
                  className="text-gray-400 hover:text-gray-600"
                  aria-label="Fechar modal"
                >
                  ×
                </button>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div>
                  <label htmlFor={fieldKey} className="block text-sm font-medium text-gray-700 mb-1">
                    {config.fieldLabel}
                  </label>
                  <input
                    id={fieldKey}
                    type="password"
                    {...register(fieldKey)}
                    defaultValue={currentValue}
                    placeholder={config.placeholder}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {fieldError?.message && (
                    <p className="text-red-500 text-sm mt-1">
                      {fieldError.message}
                    </p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    {config.helperText}
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setActiveModal(null)}
                    className="w-full sm:flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Cancelar
                  </button>
                  {settings?.[fieldKey] && (
                    <button
                      type="button"
                      onClick={() => removeIntegration(activeModal)}
                      disabled={isSubmitting}
                      className="w-full sm:flex-1 px-4 py-2 text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
                    >
                      Remover
                    </button>
                  )}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full sm:flex-1 px-4 py-2 text-white rounded-md disabled:opacity-50 ${config.buttonClass}`}
                  >
                    {isSubmitting ? 'Salvando...' : 'Salvar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        );
      })()}
    </>
  );
}





